// Tracking → Lanes tab.
//
// Per-destination-state lane scorecards. Origin is implicit (the 3PL
// warehouse) so each row is one destination state. Per row: volume,
// transit percentiles (combined + Std + Express), exception rate, and
// a vs-overall-average delta column that surfaces lanes meaningfully
// slower/faster than the overall median.
//
// International orders are bucketed as "INTL" at the bottom — they
// genuinely have no usable transit-time data (AP hands off to the
// destination-country postal carrier outside its scan network).

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Loader2, Route, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import { getTrackingLanes } from '../../lib/api'

function formatNum(v)  { return v == null ? '—' : new Intl.NumberFormat('en-AU').format(v) }
function formatDays(v) { return v == null ? '—' : `${v.toFixed(1)}d` }
function formatPct(v)  { return v == null ? '—' : `${v.toFixed(2)}%` }
function formatDelta(v) {
  if (v == null) return '—'
  const sign = v > 0 ? '+' : ''
  return `${sign}${v.toFixed(1)}d`
}

const COLS = [
  { key: 'destination_state',  label: 'Lane',           align: 'left'  },
  { key: 'shipped',            label: 'Shipped',        align: 'right' },
  { key: 'all_median',         label: 'Median',         align: 'right' },
  { key: 'all_p90',            label: 'P90',            align: 'right' },
  { key: 'std_median',         label: 'Std median',     align: 'right' },
  { key: 'exp_median',         label: 'Exp median',     align: 'right' },
  { key: 'vs_overall_days',    label: 'vs overall',     align: 'right' },
  { key: 'exception_rate_pct', label: 'Exception',      align: 'right' },
]

// Pull comparable numbers out of the nested lane row for sorting.
function getColValue(row, key) {
  if (key === 'all_median') return row.transit_days?.all?.median
  if (key === 'all_p90')    return row.transit_days?.all?.p90
  if (key === 'std_median') return row.transit_days?.standard?.median
  if (key === 'exp_median') return row.transit_days?.express?.median
  return row[key]
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={11} className="text-slate-300 ml-1 inline-block" />
  return sortDir === 'asc'
    ? <ChevronUp size={11} className="text-blue-500 ml-1 inline-block" />
    : <ChevronDown size={11} className="text-blue-500 ml-1 inline-block" />
}

export default function TrackingLanesTab() {
  const [days, setDays] = useState(30)
  const [params] = useSearchParams()
  const clientId = params.get('client_id') || undefined
  const [sortCol, setSortCol] = useState('all_median')
  const [sortDir, setSortDir] = useState('asc')

  const { data, isLoading } = useQuery({
    queryKey: ['tracking', 'lanes', days, clientId],
    queryFn:  () => getTrackingLanes({ days, client_id: clientId }),
    refetchInterval: 60_000,
  })

  const lanes = data?.lanes || []
  const overallMedian = data?.overall_median

  // Keep INTL pinned to the bottom regardless of sort direction — it's
  // not really comparable on transit time.
  const sorted = useMemo(() => {
    const intl   = lanes.filter(l => l.destination_state === 'INTL')
    const local  = lanes.filter(l => l.destination_state !== 'INTL')
    const direction = sortDir === 'asc' ? 1 : -1
    local.sort((a, b) => {
      const av = getColValue(a, sortCol)
      const bv = getColValue(b, sortCol)
      if (av == null && bv == null) return 0
      if (av == null) return 1                              // nulls last
      if (bv == null) return -1
      if (typeof av === 'string') return av.localeCompare(bv) * direction
      return (av - bv) * direction
    })
    return [...local, ...intl]
  }, [lanes, sortCol, sortDir])

  function handleSort(key) {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir(key === 'destination_state' ? 'asc' : 'asc') }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading lanes…
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-slate-500">
          Per-state lane baselines from {lanes.length} lanes in the last {days} days.
          {overallMedian != null && (
            <> Overall median transit: <span className="font-medium text-slate-700">{formatDays(overallMedian)}</span></>
          )}
        </p>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-white text-xs">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={[
                'px-3 py-1.5 font-medium transition-colors',
                d === days ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50',
              ].join(' ')}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider">
              <tr>
                {COLS.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className={`px-3 py-2 font-semibold cursor-pointer select-none ${col.align === 'right' ? 'text-right' : 'text-left'} hover:text-slate-700`}
                  >
                    {col.label}
                    <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.length === 0 ? (
                <tr><td colSpan={COLS.length} className="px-3 py-10 text-center text-slate-400">No lane data in this window.</td></tr>
              ) : sorted.map(l => {
                const t = l.transit_days
                const isIntl = l.destination_state === 'INTL'
                const deltaAccent = l.vs_overall_days == null ? 'text-slate-400' :
                                    l.vs_overall_days > 1.5  ? 'text-red-600 font-medium' :
                                    l.vs_overall_days < -0.5 ? 'text-emerald-600 font-medium' :
                                                               'text-slate-500'
                const excpAccent = l.exception_rate_pct > 1 ? 'text-red-600' :
                                   l.exception_rate_pct > 0 ? 'text-amber-600' : 'text-slate-500'
                return (
                  <tr key={l.destination_state} className={isIntl ? 'bg-slate-50/50' : ''}>
                    <td className="px-3 py-2 font-medium text-slate-700">
                      <span className="inline-flex items-center gap-1.5">
                        <Route size={11} className="text-slate-400" />
                        {l.destination_state}
                        {isIntl && <span className="text-[10px] text-slate-400 italic">(limited tracking)</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700 tabular-nums">{formatNum(l.shipped)}</td>
                    <td className="px-3 py-2 text-right text-slate-700 tabular-nums font-medium">{formatDays(t.all.median)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{formatDays(t.all.p90)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{formatDays(t.standard.median)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 tabular-nums">{formatDays(t.express.median)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${deltaAccent}`}>{formatDelta(l.vs_overall_days)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${excpAccent}`}>{formatPct(l.exception_rate_pct)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
